(() => {
  'use strict';
  const createQuery = "mutation ShippingQuote($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {\n  cartCreate(input: $input) {\n    cart {\n      id\n      totalQuantity\n      cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } totalAmountEstimated }\n      deliveryGroups(first: 10) {\n        nodes { id selectedDeliveryOption { handle } deliveryOptions { handle title estimatedCost { amount currencyCode } } }\n        pageInfo { hasNextPage }\n      }\n      discountCodes { code applicable }\n    }\n    userErrors { field message }\n    warnings { code message }\n  }\n}\n";
  const selectQuery = "mutation SelectQuoteDelivery($cartId: ID!, $options: [CartSelectedDeliveryOptionInput!]!, $country: CountryCode!) @inContext(country: $country) {\n  cartSelectedDeliveryOptionsUpdate(cartId: $cartId, selectedDeliveryOptions: $options) {\n    cart {\n      cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } totalAmountEstimated }\n    }\n    userErrors { field message }\n    warnings { code message }\n  }\n}\n";
  const instances = new Set();
  const pending = new Map();
  let settings;
  const endpoint = '/api/2026-07/graphql.json';
  const readDestination = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem('tbs_delivery_destination') || 'null');
      return value && value.country === settings.country ? value : null;
    } catch { return null; }
  };
  const saveDestination = value => {
    try { sessionStorage.setItem('tbs_delivery_destination', JSON.stringify(value)); } catch {}
  };
  const api = async (query, variables, signal) => {
    const response = await fetch(endpoint, {
      method: 'POST', credentials: 'omit', signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables })
    });
    if (!response.ok) throw new Error('API_UNAVAILABLE');
    const data = await response.json();
    if (data.errors?.length) throw new Error('API_UNAVAILABLE');
    return data.data;
  };
  const fingerprint = cart => JSON.stringify({currency:cart.currency, items:(cart.items || []).map(item=>({key:item.key,id:item.variant_id,quantity:item.quantity,properties:item.properties})),total:cart.total_price});
  const currentCart = async signal => {
    const response = await fetch(settings.root.replace(/\/?$/, '/') + 'cart.js', {
      credentials: 'same-origin', cache: 'no-store', signal
    });
    if (!response.ok) throw new Error('CART_UNAVAILABLE');
    return response.json();
  };
  const makeLines = items => items.map(item => {
    const line = {
      merchandiseId: 'gid://shopify/ProductVariant/' + item.variant_id,
      quantity: item.quantity
    };
    if (item.selling_plan_allocation?.selling_plan?.id) {
      line.sellingPlanId = 'gid://shopify/SellingPlan/' + item.selling_plan_allocation.selling_plan.id;
    }
    const attributes = Object.entries(item.properties || {}).filter(([, value]) => value != null)
      .map(([key, value]) => ({ key, value: String(value) }));
    if (attributes.length) line.attributes = attributes;
    return line;
  });
  const money = value => {
    const number = Number(value.amount);
    if (!Number.isFinite(number)) throw new Error('INVALID_AMOUNT');
    return new Intl.NumberFormat(settings.locale, {
      style: 'currency', currency: value.currencyCode, currencyDisplay: 'code'
    }).format(number);
  };
  const quoteCart = async (input, country) => {
    const key = JSON.stringify({ country, input });
    if (pending.has(key)) return pending.get(key);
    const request = api(createQuery, { input, country }).then(data => {
      const payload = data.cartCreate;
      if (payload.userErrors?.length || !payload.cart) throw new Error('QUOTE_UNAVAILABLE');
      const requested = input.lines.reduce((sum, line) => sum + line.quantity, 0);
      if (payload.cart.totalQuantity !== requested || payload.warnings?.length) {
        throw new Error('PRODUCT_UNAVAILABLE');
      }
      return payload.cart;
    });
    pending.set(key, request);
    try { return await request; } finally { pending.delete(key); }
  };
  class DeliveryQuote {
    constructor(element) {
      this.element = element;
      this.mode = element.dataset.mode;
      this.product = JSON.parse(element.querySelector('[data-delivery-product]').textContent);
      this.country = element.querySelector('[data-delivery-country]');
      this.province = element.querySelector('[data-delivery-province]');
      this.postal = element.querySelector('[data-delivery-postal]');
      this.result = element.querySelector('[data-delivery-result]');
      this.status = element.querySelector('[data-delivery-status]');
      this.button = element.querySelector('[data-delivery-calculate]');
      this.scope = element.querySelector('[data-delivery-scope]');
      this.services = element.querySelector('[data-delivery-services]');
      this.sequence = 0;
      this.quote = null;
      this.timer = null;
      const saved = readDestination();
      this.fillProvinces(saved?.province);
      this.postal.value = saved?.zip || '';
      this.country.addEventListener('change', () => this.changeMarket());
      this.button.addEventListener('click', () => this.calculate(true));
      this.element.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('input,select')) {
          event.preventDefault();
          this.calculate(true);
        }
      });
      this.province.addEventListener('change', () => this.schedule());
      this.postal.addEventListener('input', () => this.invalidate(settings.strings.initial));
      this.postal.addEventListener('change', () => this.schedule());
      if (this.mode === 'product') {
        this.form = document.getElementById(element.dataset.formId);
        this.section = element.closest('[data-section-type="product"]') || element.closest('.product-section') || this.form?.closest('section') || element.parentElement;
        this.section.addEventListener('change', event => {
          if (!this.element.contains(event.target)) this.schedule();
        });
        this.section.addEventListener('click', event => {
          if (!this.element.contains(event.target) && event.target.closest('.js-qty__adjust')) this.schedule();
        });
        document.addEventListener('variant:change', event => {
          if (!event.detail?.sectionId || this.section.contains(event.target) || event.detail.sectionId === this.section.dataset.sectionId) this.schedule();
        });
      }
      instances.add(this);
      if (saved?.zip) this.schedule();
    }
    visible() {
      return this.element.isConnected && this.element.getClientRects().length > 0;
    }
    fillProvinces(selected) {
      const source = document.getElementById('TbsDeliveryCountryData');
      const option = Array.from(source.options).find(item => item.dataset.countryCode === settings.country)
        || Array.from(source.options).find(item => item.value === this.country.selectedOptions[0]?.textContent.split(' · ')[0]);
      const provinces = JSON.parse(option?.dataset.provinces || '[]');
      this.province.replaceChildren();
      const empty = document.createElement('option');
      empty.value = '';
      empty.textContent = settings.strings.province;
      this.province.append(empty);
      for (const [code, name] of provinces) {
        const item = document.createElement('option');
        item.value = code;
        item.textContent = name;
        this.province.append(item);
      }
      this.province.value = selected || '';
      this.element.querySelector('[data-delivery-province-wrap]').hidden = !provinces.length;
    }
    changeMarket() {
      this.invalidate(settings.strings.loading);
      const form = document.getElementById('TbsDeliveryLocalization');
      form.querySelector('[name="country_code"]').value = this.country.value;
      form.querySelector('[name="return_to"]').value = location.pathname + location.search;
      saveDestination({ country: this.country.value, province: '', zip: '' });
      form.submit();
    }
    invalidate(message) {
      this.sequence++;
      this.quote = null;
      this.result.hidden = true;
      this.status.textContent = message;
      this.element.setAttribute('aria-busy', 'false');
      this.button.disabled = false;
    }
    schedule() {
      this.invalidate(settings.strings.initial);
      clearTimeout(this.timer);
      if (!this.postal.value.trim()) return;
      this.timer = setTimeout(() => {
        if (this.visible()) this.calculate(false);
      }, 400);
    }
    destination() {
      const address = { countryCode: settings.country, zip: this.postal.value.trim() };
      if (!this.element.querySelector('[data-delivery-province-wrap]').hidden && this.province.value) {
        address.provinceCode = this.province.value;
      }
      return address;
    }
    async calculate(broadcast) {
      const address = this.destination();
      if (!address.zip || (!this.element.querySelector('[data-delivery-province-wrap]').hidden && !address.provinceCode)) {
        this.invalidate(settings.strings.missing);
        return;
      }
      const destination = { country: settings.country, province: address.provinceCode || '', zip: address.zip };
      saveDestination(destination);
      if (broadcast) {
        for (const other of instances) {
          if (other !== this) {
            other.postal.value = destination.zip;
            other.province.value = destination.province;
            other.schedule();
          }
        }
      }
      const sequence = ++this.sequence;
      this.quote = null;
      this.result.hidden = true;
      this.status.textContent = settings.strings.loading;
      this.element.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      try {
        const cart = await currentCart();
        this.cartFingerprint = fingerprint(cart);
        let lines = makeLines(cart.items || []);
        let includeProduct = false;
        if (this.mode === 'product') {
          const variantInput = this.form?.querySelector('[name="id"]');
          const variantId = Number(variantInput?.value || this.product.variantId);
          const variant = this.product.variants.find(item => Number(item.id) === variantId);
          const quantityInput = this.section.querySelector('[name="quantity"]');
          const quantity = Number(quantityInput?.value || 1);
          if (!variant?.available || !Number.isInteger(quantity) || quantity < 1) throw new Error('PRODUCT_UNAVAILABLE');
          lines.push({ merchandiseId: 'gid://shopify/ProductVariant/' + variantId, quantity });
          includeProduct = true;
        }
        if (!lines.length) throw new Error('EMPTY_CART');
        if (lines.length > 250) throw new Error('LARGE_CART');
        const discounts = (cart.discount_codes || []).map(item => typeof item === 'string' ? item : item.code).filter(Boolean);
        const input = {
          lines, buyerIdentity: { countryCode: settings.country }, discountCodes: discounts,
          delivery: { addresses: [{ address: { deliveryAddress: address },
            selected: true, oneTimeUse: true, validationStrategy: 'COUNTRY_CODE_ONLY' }] }
        };
        const quote = await quoteCart(input, settings.country);
        if (sequence !== this.sequence) return;
        if (quote.deliveryGroups.pageInfo.hasNextPage) throw new Error('LARGE_CART');
        const groups = quote.deliveryGroups.nodes;
        if (!groups.length || groups.some(group => !group.deliveryOptions.length)) throw new Error('NO_RATES');
        this.quote = quote;
        this.scope.textContent = includeProduct
          ? (cart.item_count ? settings.strings.combined_scope : settings.strings.product_scope)
          : settings.strings.cart_scope;
        this.services.replaceChildren();
        for (const [index, group] of groups.entries()) {
          const label = document.createElement('label');
          label.textContent = settings.strings.service + (groups.length > 1 ? ' ' + (index + 1) : '');
          label.className = 'tbs-delivery__service';
          const select = document.createElement('select');
          select.dataset.deliveryGroupIndex = String(index);
          select.setAttribute('aria-label', label.textContent);
          for (const option of group.deliveryOptions) {
            const item = document.createElement('option');
            item.value = option.handle;
            item.textContent = (option.title || settings.strings.service) + ' · ' + money(option.estimatedCost);
            select.append(item);
          }
          const cheapest = [...group.deliveryOptions].sort((a, b) => Number(a.estimatedCost.amount) - Number(b.estimatedCost.amount))[0];
          select.value = group.selectedDeliveryOption?.handle || cheapest.handle;
          select.addEventListener('change', () => this.selectServices());
          label.append(select);
          this.services.append(label);
        }
        const needsSelection = groups.some(group => !group.selectedDeliveryOption);
        if (needsSelection) await this.selectServices(sequence);
        else this.render(quote.cost);
      } catch (error) {
        if (sequence !== this.sequence) return;
        const messages = {
          PRODUCT_UNAVAILABLE: settings.strings.unavailable,
          EMPTY_CART: settings.strings.empty,
          LARGE_CART: settings.strings.large,
          NO_RATES: settings.strings.no_rates
        };
        this.result.hidden = true;
        this.status.textContent = messages[error.message] || settings.strings.error;
      } finally {
        if (sequence === this.sequence) {
          this.element.setAttribute('aria-busy', 'false');
          this.button.disabled = false;
        }
      }
    }
    async selectServices(existingSequence) {
      if (!this.quote) return;
      const sequence = existingSequence ?? ++this.sequence;
      this.result.hidden = true;
      this.status.textContent = settings.strings.loading;
      this.element.setAttribute('aria-busy', 'true');
      const options = Array.from(this.services.querySelectorAll('select')).map((select, index) => ({
        deliveryGroupId: this.quote.deliveryGroups.nodes[index].id, deliveryOptionHandle: select.value
      }));
      try {
        const data = await api(selectQuery, { cartId: this.quote.id, options, country: settings.country });
        if (sequence !== this.sequence) return;
        const payload = data.cartSelectedDeliveryOptionsUpdate;
        if (payload.userErrors?.length || payload.warnings?.length || !payload.cart) throw new Error('QUOTE_UNAVAILABLE');
        this.quote.cost = payload.cart.cost;
        this.render(payload.cart.cost);
      } catch {
        if (sequence === this.sequence) {
          this.result.hidden = true;
          this.status.textContent = settings.strings.error;
        }
      } finally {
        if (sequence === this.sequence) this.element.setAttribute('aria-busy', 'false');
      }
    }
    render(cost) {
      const selected = Array.from(this.services.querySelectorAll('select')).map((select, index) =>
        this.quote.deliveryGroups.nodes[index].deliveryOptions.find(option => option.handle === select.value));
      const currency = cost.totalAmount.currencyCode;
      if (currency !== settings.currency || selected.some(option => option.estimatedCost.currencyCode !== currency)) {
        throw new Error('CURRENCY_MISMATCH');
      }
      const amount = selected.reduce((sum, option) => sum + Number(option.estimatedCost.amount), 0);
      this.element.querySelector('[data-delivery-subtotal]').textContent = money(cost.subtotalAmount);
      this.element.querySelector('[data-delivery-shipping]').textContent = money({ amount, currencyCode: currency });
      this.element.querySelector('[data-delivery-total]').textContent = money(cost.totalAmount);
      this.element.querySelector('[data-delivery-duties]').textContent = selected.every(option =>
        /all duties\s*(?:&|and)\s*taxes included/i.test(option.title || ''))
        ? settings.strings.duties_included : settings.strings.duties_unknown;
      this.status.textContent = settings.strings.ready;
      this.result.hidden = false;
    }
  }
  const mount = () => {
    const node = document.getElementById('TbsDeliverySettings');
    if (!node) return;
    settings = JSON.parse(node.textContent);
    for (const element of document.querySelectorAll('[data-tbs-delivery]')) {
      if (!element.dataset.deliveryMounted) {
        element.dataset.deliveryMounted = 'true';
        new DeliveryQuote(element);
      }
    }
  };
  let refreshing = false;
  const refresh = async () => {
    mount();
    if (refreshing) return;
    refreshing = true;
    try {
      const cart = await currentCart();
      const value = fingerprint(cart);
      for (const instance of instances) {
        if (!instance.element.isConnected) instances.delete(instance);
        else if (instance.cartFingerprint !== value) {
          instance.cartFingerprint = value;
          instance.schedule();
        }
      }
    } catch {} finally { refreshing = false; }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
  else mount();
  for (const name of ['cart:updated', 'cart:build', 'ajaxProduct:added', 'shopify:section:load']) {
    document.addEventListener(name, refresh);
  }
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refresh();
  });
  const observer = new MutationObserver(mutations => {
    if (mutations.some(mutation => mutation.target.closest?.('[data-subtotal]'))) refresh();
  });
  document.addEventListener('DOMContentLoaded', () => {
    for (const element of document.querySelectorAll('[data-subtotal]')) observer.observe(element, { childList: true, subtree: true, characterData: true });
  });
})();

